import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { HeartHandshake, Clock, CheckCircle2, XCircle, Wallet, TrendingUp, FileText, Loader2 } from "lucide-react";

const money = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/**
 * Iter 68.64 — Painel Financeiro Convênio (6 estados).
 *
 * Cada card é derivado do fluxo REAL de convênios (`bookings.status`,
 * `insurance_claims.status`, `payment_source="insurance"`). Nenhum campo
 * manual desconectado. Segue o ciclo:
 *   Cobertura → Elegibilidade → Autorização → Atendimento → Faturamento →
 *   Recebimento → Repasse.
 *
 * Endpoint: GET /api/professional/me/finance/insurance
 */

const ICONS = {
  a_faturar:       FileText,
  em_analise:      Clock,
  aprovado:        CheckCircle2,
  glosado:         XCircle,
  recebido:        Wallet,
  repasse_previsto: TrendingUp,
};

const CARD_STYLE = {
  a_faturar:       { ring: "ring-blue-200",    text: "text-blue-700",    bg: "bg-blue-50/50" },
  em_analise:      { ring: "ring-amber-200",   text: "text-amber-700",   bg: "bg-amber-50/50" },
  aprovado:        { ring: "ring-emerald-200", text: "text-emerald-700", bg: "bg-emerald-50/50" },
  glosado:         { ring: "ring-red-200",     text: "text-red-700",     bg: "bg-red-50/50" },
  recebido:        { ring: "ring-violet-200",  text: "text-violet-700",  bg: "bg-violet-50/50" },
  repasse_previsto:{ ring: "ring-teal-200",    text: "text-teal-700",    bg: "bg-teal-50/50" },
};

const STATUS_HINT = {
  eligibility_pending:   "Aguardando elegibilidade",
  authorization_pending: "Aguardando autorização",
  eligible:              "Convênio confirmado",
  authorized:            "Autorização emitida",
  completed:             "Consulta realizada",
  denied:                "Convênio recusado",
};

export const ProfessionalInsuranceFinancePanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openBucket, setOpenBucket] = useState(null);

  useEffect(() => {
    api.get("/professional/me/finance/insurance")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Erro ao carregar painel"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-slate-500 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Carregando painel de convênios…
      </div>
    );
  }
  if (!data) return null;

  const b = data.buckets || {};
  const order = data.order || Object.keys(b);
  const labels = data.labels || {};

  return (
    <section className="space-y-4" data-testid="prof-insurance-finance-panel">
      <header>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-700 font-bold">
          <HeartHandshake className="w-3.5 h-3.5" /> Ciclo Convênio · Financeiro
        </div>
        <h3 className="text-xl font-display font-bold text-[#0B2B62] mt-0.5">Faturamento por convênio</h3>
        <p className="text-xs text-slate-500 mt-1 max-w-2xl leading-relaxed">
          Cada coluna reflete o estado real das suas consultas por convênio. Cobertura → Elegibilidade →
          Autorização → Atendimento → Faturamento → Recebimento → Repasse. Consultas particulares
          seguem sendo mostradas em <b>Financeiro geral</b> (Stripe/PIX).
        </p>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {order.map((key) => {
          const bucket = b[key] || { count: 0, session_value_total: 0, prof_share_total: 0, items: [] };
          const Icon = ICONS[key] || FileText;
          const style = CARD_STYLE[key] || { ring: "ring-slate-200", text: "text-slate-700", bg: "bg-slate-50" };
          const isOpen = openBucket === key;
          const primaryValue = key === "repasse_previsto" ? bucket.prof_share_total : bucket.session_value_total;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setOpenBucket(isOpen ? null : key)}
              data-testid={`insurance-bucket-${key}`}
              className={`p-3 rounded-xl bg-white ring-1 ${style.ring} ${isOpen ? "ring-2 shadow-lg" : "shadow-sm hover:shadow-md"} text-left transition ${isOpen ? style.bg : ""}`}>
              <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold ${style.text}`}>
                <Icon className="w-3.5 h-3.5" /> {labels[key] || key}
              </div>
              <div className="mt-2 text-xl font-black text-[#0B2B62] tabular-nums">
                {money(primaryValue)}
              </div>
              <div className="mt-0.5 text-[11px] text-slate-500">
                <b>{bucket.count}</b> {bucket.count === 1 ? "consulta" : "consultas"}
                {key !== "repasse_previsto" && bucket.prof_share_total > 0 && (
                  <> · repasse {money(bucket.prof_share_total)}</>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Drill-down */}
      {openBucket && b[openBucket] && b[openBucket].items && b[openBucket].items.length > 0 && (
        <div className="border border-slate-200 rounded-xl bg-white overflow-x-auto" data-testid={`insurance-bucket-detail-${openBucket}`}>
          <div className="px-4 py-2.5 border-b bg-slate-50 flex items-center justify-between">
            <div className="text-xs font-bold text-slate-700 uppercase tracking-widest">
              {labels[openBucket]} · Detalhamento ({b[openBucket].items.length})
            </div>
            <button onClick={() => setOpenBucket(null)} className="text-xs text-slate-500 hover:text-slate-800">Fechar ×</button>
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left px-4 py-2">Data</th>
                <th className="text-left px-4 py-2">Paciente</th>
                <th className="text-left px-4 py-2">Operadora / Plano</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Valor</th>
                <th className="text-right px-4 py-2">Repasse</th>
              </tr>
            </thead>
            <tbody>
              {b[openBucket].items.map((it) => (
                <tr key={it.booking_id} className="border-t">
                  <td className="px-4 py-2 text-xs text-slate-600 tabular-nums">{(it.scheduled_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-2 text-slate-800">{it.family_display}</td>
                  <td className="px-4 py-2 text-xs text-slate-600">
                    <div className="font-semibold text-slate-700">{it.insurer_name || "—"}</div>
                    <div className="text-[11px] text-slate-500">{it.plan_name || "—"}</div>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-600">{STATUS_HINT[it.status] || it.status}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-semibold text-slate-800">{money(it.session_value)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-emerald-700 font-semibold">{money(it.professional_share)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[11px] text-slate-500 leading-relaxed max-w-3xl">
        <b>Como cada coluna é calculada:</b><br />
        <b>A faturar</b> — consultas realizadas por convênio sem guia TISS emitida ainda.<br />
        <b>Em análise</b> — reservas aguardando aprovação de elegibilidade ou autorização.<br />
        <b>Aprovado</b> — reservas com convênio confirmado, prontas pra consulta ou já faturadas mas ainda não pagas.<br />
        <b>Glosado</b> — reservas ou guias recusadas pela operadora.<br />
        <b>Recebido</b> — guias efetivamente pagas pela operadora à RAF.<br />
        <b>Repasse previsto</b> — sua parte (65%) sobre consultas em Aprovado/A faturar/Recebido ainda não repassadas.
      </p>
    </section>
  );
};

export default ProfessionalInsuranceFinancePanel;
